/**
 * Client and staff reads for AI-tools Consent.
 * Writes go through the form-submission bot.
 */
import type { MedplumClient } from '@medplum/core';
import type { Consent, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  AI_TOOLS_CONSENT_CATEGORY_CODE,
  AI_TOOLS_CONSENT_QUESTIONNAIRE_URL,
  CONSENT_CATEGORY_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  resolveAiToolsConsentState,
  type AiToolsConsentState,
} from '@unify/fhir-model/resources/consent';

export async function searchAllAiToolsConsentsForPatient(
  medplum: MedplumClient,
  patientId: string,
): Promise<Consent[]> {
  medplum.invalidateSearches('Consent');
  const category = `${CONSENT_CATEGORY_SYSTEM}|${AI_TOOLS_CONSENT_CATEGORY_CODE}`;
  const pageSize = 100;
  const all: Consent[] = [];
  for (let offset = 0; ; offset += pageSize) {
    const page = await medplum.searchResources('Consent', {
      patient: `Patient/${patientId}`,
      category,
      _count: String(pageSize),
      _offset: String(offset),
    });
    all.push(...page);
    if (page.length < pageSize) break;
  }
  return all;
}

export async function loadAiToolsConsentState(
  medplum: MedplumClient,
  patientId: string,
): Promise<AiToolsConsentState> {
  const consents = await searchAllAiToolsConsentsForPatient(medplum, patientId);
  let responses: QuestionnaireResponse[] = [];
  try {
    medplum.invalidateSearches('QuestionnaireResponse');
    responses = await medplum.searchResources('QuestionnaireResponse', {
      subject: `Patient/${patientId}`,
      questionnaire: AI_TOOLS_CONSENT_QUESTIONNAIRE_URL,
      status: 'completed',
      _count: '20',
      _sort: '-authored',
    });
  } catch {
    responses = [];
  }
  return resolveAiToolsConsentState(consents, patientId, responses);
}
